import React, { useContext, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiLock, FiMinus, FiPlus, FiTrash2 } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/cartpage.scss";

function CartPage() {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity } = useContext(CartContext);

  const lines = useMemo(
    () =>
      cartItems
        .map((item) => ({ ...item, product: productData.find((product) => product.id === item.id) }))
        .filter((item) => item.product),
    [cartItems]
  );
  const subtotal = lines.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const shipping = subtotal >= siteConfig.shipping.freeThreshold ? 0 : siteConfig.shipping.standardCost;
  const amountToFreeShipping = Math.max(0, siteConfig.shipping.freeThreshold - subtotal);
  const progress = Math.min(100, (subtotal / siteConfig.shipping.freeThreshold) * 100);

  if (!lines.length) {
    return (
      <main className="cart-page cart-page--empty">
        <div className="cart-empty">
          <p className="eyebrow">YOUR BAG</p>
          <h1>Your bag is empty.</h1>
          <p>Take your time. The right pieces are waiting in the collection.</p>
          <Link className="button button--dark" to="/shop">
            Discover the collection
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="cart-page">
      <header className="cart-page__header">
        <p className="eyebrow">YOUR SELECTION</p>
        <h1>Shopping bag</h1>
        <span>{lines.reduce((sum, item) => sum + item.quantity, 0)} items</span>
      </header>

      <div className="cart-page__layout">
        <section className="cart-lines" aria-label="Items in your bag">
          <div className="shipping-progress">
            <p>
              {amountToFreeShipping > 0
                ? `${formatPrice(amountToFreeShipping)} away from complimentary delivery`
                : "You have complimentary delivery"}
            </p>
            <div><span style={{ width: `${progress}%` }} /></div>
          </div>

          {lines.map(({ product, quantity, size, color }) => (
            <article className="cart-line" key={`${product.id}-${size}-${color}`}>
              <Link className="cart-line__image" to={`/shopDetails/${product.id}`}>
                <img src={product.image} alt={product.name} />
              </Link>
              <div className="cart-line__body">
                <div className="cart-line__top">
                  <div>
                    <Link to={`/shopDetails/${product.id}`}>{product.name}</Link>
                    <p>{color} · Size {size}</p>
                  </div>
                  <span>{formatPrice(product.price * quantity)}</span>
                </div>
                <div className="cart-line__bottom">
                  <div className="quantity-stepper" aria-label={`Quantity for ${product.name}`}>
                    <button
                      type="button"
                      aria-label="Decrease quantity"
                      onClick={() =>
                        quantity === 1
                          ? removeFromCart(product.id, size, color)
                          : updateQuantity(product.id, size, color, quantity - 1)
                      }
                    >
                      <FiMinus />
                    </button>
                    <span>{quantity}</span>
                    <button
                      type="button"
                      aria-label="Increase quantity"
                      onClick={() => updateQuantity(product.id, size, color, quantity + 1)}
                    >
                      <FiPlus />
                    </button>
                  </div>
                  <button
                    type="button"
                    className="cart-line__remove"
                    onClick={() => removeFromCart(product.id, size, color)}
                  >
                    <FiTrash2 /> Remove
                  </button>
                </div>
              </div>
            </article>
          ))}

          <Link className="text-link" to="/shop">Continue shopping</Link>
        </section>

        <aside className="cart-summary">
          <h2>Order summary</h2>
          <div className="cart-summary__row">
            <span>Subtotal</span>
            <span>{formatPrice(subtotal)}</span>
          </div>
          <div className="cart-summary__row">
            <span>Standard delivery</span>
            <span>{shipping ? formatPrice(shipping) : "Complimentary"}</span>
          </div>
          <div className="cart-summary__total">
            <div>
              <span>Total</span>
              <small>Taxes included</small>
            </div>
            <strong>{formatPrice(subtotal + shipping)}</strong>
          </div>
          <button type="button" className="button button--dark" onClick={() => navigate("/checkout")}>
            Checkout
          </button>
          <p className="cart-summary__secure"><FiLock /> Secure encrypted payment</p>
          <div className="cart-summary__note">
            <h3>Returns made simple</h3>
            <p>Try your pieces at home. Returns are complimentary for {siteConfig.shipping.returnsWindow} days.</p>
            <Link to="/returns">Learn more</Link>
          </div>
        </aside>
      </div>
    </main>
  );
}

export default CartPage;
